import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toastService } from '../services/toast';
import { profileApi } from '../services/api/profile.api';
import { UserCircleIcon, BookOpenIcon, CreditCardIcon, PencilIcon, CheckCircleIcon, LockClosedIcon, BellIcon } from '@heroicons/react/outline';
import { XIcon } from '@heroicons/react/solid';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Tooltip from '../components/common/Tooltip';
import Modal from '../components/common/Modal';
import MyCourses, { CoursesSkeleton } from './MyCourses';
import Payments, { PaymentsSkeleton } from './Payments';
import { Tab } from '@headlessui/react';

export default function Profile() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'profile');
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [isEditingBio, setIsEditingBio] = useState(false);
    const [showAvatarModal, setShowAvatarModal] = useState(false);
    const [selectedAvatar, setSelectedAvatar] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    
    // مجموعة الصور الرمزية المتاحة
    const avatarOptions = [
        { id: 'avatar1', src: '/images/avatars/avatar1.png', alt: 'صورة رمزية 1' },
        { id: 'avatar2', src: '/images/avatars/avatar2.png', alt: 'صورة رمزية 2' },
        { id: 'avatar3', src: '/images/avatars/avatar3.png', alt: 'صورة رمزية 3' },
        { id: 'avatar4', src: '/images/avatars/avatar4.png', alt: 'صورة رمزية 4' },
        { id: 'avatar5', src: '/images/avatars/avatar5.png', alt: 'صورة رمزية 5' },
        { id: 'avatar6', src: '/images/avatars/avatar6.png', alt: 'صورة رمزية 6' },
        { id: 'avatar7', src: '/images/avatars/avatar7.png', alt: 'صورة رمزية 7' },
        { id: 'avatar8', src: '/images/avatars/avatar8.png', alt: 'صورة رمزية 8' },
    ];

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const [profile, setProfile] = useState({
        name: '',
        email: '',
        phone: '',
        avatar: null,
        isEmailVerified: false,
        needsEmailVerification: false,
        joinDate: '',
        bio: '',
        notifications: true
    });

    useEffect(() => {
        fetchProfile();
    }, []);

    const fetchProfile = async () => {
        try {
            setLoading(true);
            const response = await profileApi.getProfile();
            // تحقق من وجود البيانات في الاستجابة
            if (response && response.user) {
                const userData = response.user;
                setProfile({
                    name: userData.name || '',
                    email: userData.email || '',
                    phone: userData.phone || '',
                    avatar: userData.avatar || null,
                    isEmailVerified: userData.email_verified_at !== null,
                    needsEmailVerification: userData.email_verified_at === null,
                    joinDate: userData.created_at || '',
                    bio: userData.bio || '',
                    notifications: userData.notifications_enabled || true
                });
                // User data loaded successfully
            } else {
                // User data not available in response
            }
            setLoading(false);
        } catch (error) {
            // Error loading user data
            toastService.error('حدث خطأ في تحميل البيانات');
            setLoading(false);
        }
    };

    const handleEditClick = () => {
        setIsEditing(true);
    };

    const handleSaveClick = async () => {
        try {
            setLoading(true);
            const data = {
                name: profile.name,
                email: profile.email,
                phone: profile.phone
            };
            await profileApi.updateProfile(data);
            setIsEditing(false);
            toastService.success('تم حفظ التغييرات بنجاح');
            // تحديث البيانات بعد الحفظ
            fetchProfile();
        } catch (error) {
            // Error saving profile
            toastService.error('حدث خطأ في حفظ التغييرات');
        } finally {
            setLoading(false);
        }
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        // إعادة تحميل البيانات من السيرفر
        fetchProfile();
    };

    const handleAvatarClick = () => {
        setShowAvatarModal(true);
    };

    const handleAvatarSelect = (avatar) => {
        setSelectedAvatar(avatar.id);
    };

    const handleSaveAvatar = async () => {
        if (!selectedAvatar) {
            setShowAvatarModal(false);
            return;
        }

        try {
            setLoading(true);
            await profileApi.updateAvatar({ avatar: selectedAvatar });
            
            // تحديث البروفايل مع الصورة الجديدة
            setProfile(prev => ({
                ...prev,
                avatar: selectedAvatar
            }));
            
            toastService.success('تم تغيير الصورة الرمزية بنجاح');
            setShowAvatarModal(false);
        } catch (error) {
            // Error updating avatar
            toastService.error('حدث خطأ أثناء تحديث الصورة الرمزية');
        } finally {
            setLoading(false);
        }
    };

    const handleResendVerification = async () => {
        try {
            // TODO: Resend verification email
            await new Promise(resolve => setTimeout(resolve, 1000));
            toastService.success('تم إرسال رابط التفعيل إلى بريدك الإلكتروني');
        } catch (error) {
            toastService.error('حدث خطأ في إرسال رابط التفعيل');
        }
    };

    const handleTabChange = (tab) => {
        if (isMobile) {
            setActiveTab(activeTab === tab ? '' : tab);
        } else {
            setActiveTab(tab);
        }
        setSearchParams({ tab });
    };

    const handleSaveBio = async () => {
        try {
            setLoading(true);
            // Start saving bio
            // Current bio before saving
            
            // نجرب نستخدم النص العادي بدل HTML
            // حيث أن ReactQuill يضيف وسوم <p> للنص
            const plainText = profile.bio.replace(/<[^>]*>?/g, '');
            // Text after removing HTML
            
            // حفظ النبذة باستخدام النص العادي
            const updateResult = await profileApi.updateProfile({
                name: profile.name, // نضيف بيانات أخرى لتحسين التوافق
                email: profile.email,
                phone: profile.phone,
                bio: plainText // نرسل النص بدون HTML
            });
            
            // Bio update result
            
            // نستدعي تحديث البيانات من السيرفر
            // Updating data from server
            const freshData = await profileApi.getProfile();
            // New data from server
            
            // تحديث state البيانات يدوياً عشان نتأكد
            if (freshData && freshData.user) {
                setProfile(prev => ({
                    ...prev,
                    name: freshData.user.name || prev.name,
                    email: freshData.user.email || prev.email,
                    phone: freshData.user.phone || prev.phone,
                    bio: freshData.user.bio || prev.bio
                }));
                // Local data updated
            }
            
            setIsEditingBio(false);
            toastService.success('تم حفظ النبذة بنجاح');
            // End of bio saving
        } catch (error) {
            // Error saving bio
            toastService.error('حدث خطأ في حفظ النبذة');
        } finally {
            setLoading(false);
        }
    };

    const handleCancelBio = () => {
        setIsEditingBio(false);
        // Reset bio value
        setProfile(prevProfile => ({
            ...prevProfile,
            bio: prevProfile.bio
        }));
    };

    const handlePhoneChange = (e) => {
        const value = e.target.value;
        // السماح فقط بالأرقام و "05" في البداية
        if (value === '' || (value.startsWith('05') && /^05\d*$/.test(value) && value.length <= 10)) {
            setProfile({ ...profile, phone: value });
        }
    };

    const [showPasswordForm, setShowPasswordForm] = useState(false);
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            toastService.error('كلمة المرور الجديدة غير متطابقة');
            return;
        }
        try {
            // Password data being sent
            
            // التحقق من شروط الفاليديشن قبل الإرسال
            if (passwordData.newPassword.length < 8) {
                toastService.error('يجب أن تكون كلمة المرور 8 أحرف على الأقل');
                return;
            }
            
            // استدعاء API لتغيير كلمة المرور
            const response = await profileApi.updatePassword({
                current_password: passwordData.currentPassword,
                password: passwordData.newPassword,
                password_confirmation: passwordData.confirmPassword
            });
            
            // Server response
            toastService.success('تم تغيير كلمة المرور بنجاح');
            setShowPasswordForm(false);
            setPasswordData({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });
        } catch (error) {
            // Error changing password
            // Error details
            
            if (error.response?.status === 422) {
                // خطأ فاليديشن
                if (error.response?.data?.errors) {
                    // عرض رسالة الخطأ من السيرفر
                    const errorField = Object.keys(error.response.data.errors)[0];
                    const errorMsg = error.response.data.errors[errorField][0];
                    toastService.error(errorMsg || 'خطأ في البيانات المدخلة');
                } else if (error.response?.data?.message) {
                    toastService.error(error.response.data.message);
                } else {
                    toastService.error('حدث خطأ أثناء التحقق من البيانات');
                }
            } else {
                toastService.error('حدث خطأ أثناء تغيير كلمة المرور');
            }
        }
    };

    const toggleNotifications = async () => {
        try {
            // TODO: API call to toggle notifications
            setProfile(prev => ({
                ...prev,
                notifications: !prev.notifications
            }));
            toastService.success(profile.notifications ? 'تم إيقاف الإشعارات' : 'تم تفعيل الإشعارات');
        } catch (error) {
            toastService.error('حدث خطأ أثناء تحديث الإشعارات');
        }
    };

    const tabs = [
        { id: 'profile', name: 'الملف الشخصي', icon: UserCircleIcon },
        { id: 'courses', name: 'دوراتي وكتبي', icon: BookOpenIcon },
        { id: 'wallet', name: 'المحفظة والمدفوعات', icon: CreditCardIcon },
    ];

    // مكونات وهمية للتحميل
    const ProfileSkeleton = () => {
        return (
            <div className="animate-pulse">
                {/* هيكل التابز */}
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg mb-6">
                    <div className="border-b border-gray-200 dark:border-gray-700">
                        <nav className="flex space-x-8 space-x-reverse px-6">
                            {[...Array(3)].map((_, i) => (
                                <div key={i} className="py-4 px-1">
                                    <div className="flex items-center font-medium text-sm">
                                        <div className="h-5 w-5 ml-2 bg-gray-200 dark:bg-gray-700 rounded"></div>
                                        <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
                                    </div>
                                </div>
                            ))}
                        </nav>
                    </div>
                </div>
                
                
                {/* هيكل معلومات البروفايل */}
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden mb-6">
                    {/* القسم العلوي - الصورة والاسم */}
                    <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex flex-col md:flex-row md:items-center">
                            <div className="flex items-center space-x-4 space-x-reverse mb-4 md:mb-0">
                                {/* مكان الصورة الشخصية */}
                                <div className="w-24 h-24 rounded-full bg-gray-200 dark:bg-gray-700"></div>
                                
                                <div className="flex-1">
                                    <div className="h-6 w-40 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
                                    <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
                                </div>
                            </div>
                            
                            <div className="mt-4 md:mt-0 md:mr-auto space-y-2">
                                <div className="h-8 w-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
                            </div>
                        </div>
                    </div>
                    
                    {/* بيانات الاتصال */}
                    <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {[...Array(3)].map((_, i) => (
                                <div key={i} className="space-y-2">
                                    <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
                                    <div className="h-8 w-full bg-gray-200 dark:bg-gray-700 rounded"></div>
                                </div>
                            ))}
                        </div>
                    </div>
                    
                    {/* النبذة الشخصية */}
                    <div className="p-6">
                        <div className="space-y-4">
                            <div className="h-5 w-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
                            <div className="space-y-2">
                                <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 rounded"></div>
                                <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 rounded"></div>
                                <div className="h-4 w-3/4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-16">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <ProfileSkeleton />
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-16">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* التابز */}
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg mb-6">
                    <div className={`${!isMobile && 'border-b border-gray-200 dark:border-gray-700'}`}>
                        <nav className={`${isMobile ? 'flex flex-col' : 'flex space-x-8 space-x-reverse px-6'}`} aria-label="Tabs">
                            {tabs.map((tab) => (
                                <div key={tab.id} className="w-full">
                                    <button
                                        onClick={() => handleTabChange(tab.id)}
                                        className={`
                                            flex items-center font-medium text-sm w-full
                                            ${isMobile ? 'justify-between p-4 border-b border-gray-200 dark:border-gray-700' : 'py-4 px-1 border-b-2'}
                                            ${activeTab === tab.id
                                                ? isMobile 
                                                    ? 'text-gray-900 bg-white dark:bg-gray-800 dark:text-white'
                                                    : 'border-primary-500 text-primary-600 dark:border-primary-500 dark:text-primary-300'
                                                : isMobile
                                                    ? 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                                            }
                                        `}
                                    >
                                        <div className="flex items-center">
                                            <tab.icon className="h-5 w-5 ml-2" />
                                            {tab.name}
                                        </div>
                                        {isMobile && (
                                            <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 transform transition-transform ${activeTab === tab.id ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                            </svg>
                                        )}
                                    </button>
                                    {/* محتوى التاب - يظهر مباشرة تحت الزر في النسخة المحمولة */}
                                    {isMobile && activeTab === tab.id && (
                                        <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                                            {tab.id === 'profile' && (
                                                <div className="space-y-8">
                                                    {/* الصورة الشخصية */}
                                                    <div className="flex items-center space-x-4 space-x-reverse">
                                                        <div className="relative group">
                                                            <div className="w-24 h-24 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                                                                {profile.avatar ? (
                                                                    <img 
                                                                        src={avatarOptions.find(a => a.id === profile.avatar)?.src || '/images/avatars/avatar1.png'} 
                                                                        alt="الصورة الشخصية" 
                                                                        className="w-full h-full object-cover"
                                                                    />
                                                                ) : (
                                                                    <UserCircleIcon className="w-full h-full text-gray-400 dark:text-gray-500" />
                                                                )}
                                                            </div>
                                                            <button
                                                                onClick={handleAvatarClick}
                                                                className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 text-white opacity-0 group-hover:opacity-100 transition-opacity rounded-full"
                                                            >
                                                                <PencilIcon className="h-6 w-6" />
                                                            </button>
                                                        </div>
                                                        
                                                        <div className="flex-1">
                                                            {isEditing ? (
                                                                <div className="space-y-4">
                                                                    <div>
                                                                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                                            الاسم
                                                                        </label>
                                                                        <input
                                                                            type="text"
                                                                            id="name"
                                                                            value={profile.name}
                                                                            onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                                                                            className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 focus:border-black dark:focus:border-white focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                                        />
                                                                    </div>
                                                                    <div>
                                                                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                                            البريد الإلكتروني
                                                                        </label>
                                                                        <input
                                                                            type="email"
                                                                            id="email"
                                                                            value={profile.email}
                                                                            onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                                                                            className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 focus:border-black dark:focus:border-white focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                                            disabled
                                                                        />
                                                                    </div>
                                                                    <div>
                                                                        <label htmlFor="phone" className="block text-base font-medium text-gray-700 dark:text-gray-200 mb-2">
                                                                            رقم الجوال
                                                                        </label>
                                                                        <input
                                                                            type="tel"
                                                                            id="phone"
                                                                            value={profile.phone || ''}
                                                                            onChange={handlePhoneChange}
                                                                            placeholder="05XXXXXXXX"
                                                                            maxLength={10}
                                                                            className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 focus:border-black dark:focus:border-white focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                                            dir="ltr"
                                                                        />
                                                                    </div>
                                                                    <div className="flex items-center justify-between">
                                                                        <div>
                                                                            {isEditing ? (
                                                                                <div className="flex gap-2">
                                                                                    <Tooltip text="حفظ التغييرات">
                                                                                        <button
                                                                                            onClick={handleSaveClick}
                                                                                            className="p-2 text-green-600 hover:text-green-700 dark:text-green-500 dark:hover:text-green-400 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800"
                                                                                            disabled={loading}
                                                                                        >
                                                                                            <CheckCircleIcon className="h-5 w-5" />
                                                                                        </button>
                                                                                    </Tooltip>
                                                                                    <Tooltip text="إلغاء التعديل">
                                                                                        <button
                                                                                            onClick={handleCancelEdit}
                                                                                            className="p-2 text-red-600 hover:text-red-700 dark:text-red-500 dark:hover:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800"
                                                                                        >
                                                                                            <XIcon className="h-5 w-5" />
                                                                                        </button>
                                                                                    </Tooltip>
                                                                                </div>
                                                                            ) : (
                                                                                <Tooltip text="تعديل البيانات">
                                                                                    <button
                                                                                        onClick={handleEditClick}
                                                                                        className="p-2 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                                                                                    >
                                                                                        <PencilIcon className="h-5 w-5" />
                                                                                    </button>
                                                                                </Tooltip>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <div>
                                                                    <div className="flex items-center justify-between">
                                                                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                                                                            {profile.name}
                                                                        </h3>
                                                                        <Tooltip text="تعديل البيانات">
                                                                            <button
                                                                                onClick={handleEditClick}
                                                                                className="p-2 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                                                                            >
                                                                                <PencilIcon className="h-5 w-5" />
                                                                            </button>
                                                                        </Tooltip>
                                                                    </div>
                                                                    <div className="mt-1 space-y-2">
                                                                        <div className="flex items-center">
                                                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                                                                {profile.email}
                                                                            </p>
                                                                            {profile.isEmailVerified ? (
                                                                                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                                                                            ) : (
                                                                                <div className="flex items-center mr-2">
                                                                                    <Tooltip text="إعادة إرسال رابط التفعيل">
                                                                                        <button
                                                                                            onClick={handleResendVerification}
                                                                                            className="mr-2 text-sm text-primary-600 hover:text-primary-500 dark:text-primary-400"
                                                                                        >
                                                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
                                                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                                                            </svg>
                                                                                        </button>
                                                                                    </Tooltip>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        <p className="text-sm text-gray-500 dark:text-gray-400">
                                                                            <span style={{ direction: 'ltr', display: 'inline-block' }}>
                                                                                {profile.phone ? profile.phone.replace(/(\d{3})(\d{3})(\d{4})/, '$1 $2 $3') : 'لم يتم إضافة رقم الجوال'}
                                                                            </span>
                                                                        </p>
                                                                        <p className="text-sm text-gray-500 dark:text-gray-400">
                                                                            تاريخ التسجيل: {new Date(profile.joinDate).toLocaleDateString('ar', { 
                                                                                year: 'numeric',
                                                                                month: '2-digit',
                                                                                day: '2-digit'
                                                                            })}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* النبذة الشخصية */}
                                                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
                                                        <div className="flex items-center justify-between p-4">
                                                            <h4 className="text-base font-medium text-gray-900 dark:text-white">النبذة الشخصية</h4>
                                                            {!isEditingBio && (
                                                                <Tooltip text="تعديل النبذة">
                                                                    <button
                                                                        onClick={() => setIsEditingBio(true)}
                                                                        className="p-2 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                                                                    >
                                                                        <PencilIcon className="h-5 w-5" />
                                                                    </button>
                                                                </Tooltip>
                                                            )}
                                                            {isEditingBio && (
                                                                <div className="flex gap-2">
                                                                    <Tooltip text="حفظ النبذة">
                                                                        <button
                                                                            onClick={handleSaveBio}
                                                                            className="p-2 text-green-600 hover:text-green-700 dark:text-green-500 dark:hover:text-green-400 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800"
                                                                            disabled={loading}
                                                                        >
                                                                            <CheckCircleIcon className="h-5 w-5" />
                                                                        </button>
                                                                    </Tooltip>
                                                                    <Tooltip text="إلغاء التعديل">
                                                                        <button
                                                                            onClick={handleCancelBio}
                                                                            className="p-2 text-red-600 hover:text-red-700 dark:text-red-500 dark:hover:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800"
                                                                        >
                                                                            <XIcon className="h-5 w-5" />
                                                                        </button>
                                                                    </Tooltip>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="px-4 pb-4">
                                                            {isEditingBio ? (
                                                                <ReactQuill
                                                                    theme="snow"
                                                                    value={profile.bio}
                                                                    onChange={(content) => setProfile({ ...profile, bio: content })}
                                                                    placeholder="اكتب نبذة عن نفسك..."
                                                                    className="[&_.ql-container]:mb-0 [&_.ql-container]:h-auto [&_.ql-editor]:h-auto [&_.ql-editor]:min-h-[150px] [&_.ql-editor]:max-h-none [&_.ql-editor]:text-right [&_.ql-editor]:direction-rtl [&_.ql-toolbar]:direction-rtl [&_.ql-toolbar]:bg-white [&_.ql-toolbar]:dark:bg-gray-700 [&_.ql-toolbar]:dark:text-white [&_.ql-toolbar_.ql-stroke]:dark:stroke-white [&_.ql-toolbar_.ql-fill]:dark:fill-white [&_.ql-toolbar_.ql-picker-label]:dark:text-white [&_.ql-toolbar_.ql-picker-item]:dark:text-white [&_.ql-toolbar_.ql-picker-options]:dark:bg-gray-700 [&_.ql-toolbar.ql-snow]:border-gray-300 [&_.ql-container.ql-snow]:border-gray-300 [&_.ql-toolbar.ql-snow]:dark:border-gray-600 [&_.ql-container.ql-snow]:dark:border-gray-600 [&_.ql-editor]:text-gray-900 [&_.ql-editor]:dark:text-white [&_.ql-editor]:text-sm"
                                                                    modules={{
                                                                        toolbar: [
                                                                            ['bold', 'italic', 'underline'],
                                                                            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                                                                            ['clean']
                                                                        ]
                                                                    }}
                                                                />
                                                            ) : (
                                                                <div className="bg-gray-800 dark:bg-gray-800 p-4 rounded-lg">
                                                                    {profile.bio ? (
                                                                        <div className="text-gray-300 dark:text-gray-300 m-0 text-right" dangerouslySetInnerHTML={{ __html: profile.bio }} />
                                                                    ) : (
                                                                        <p className="text-gray-300 dark:text-gray-300 m-0 text-right">لم يتم إضافة نبذة بعد</p>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* كلمة المرور والإشعارات */}
                                                    <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg">
                                                        <div className="prose dark:prose-invert max-w-none text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700/50 p-3 rounded-lg">
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center">
                                                                    <LockClosedIcon className="h-6 w-6 text-gray-500 dark:text-gray-400 ml-2" />
                                                                    <h2 className="text-lg font-medium text-gray-900 dark:text-white">كلمة المرور والإشعارات</h2>
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    {/* زر تغيير كلمة المرور */}
                                                                    <Tooltip text="تغيير كلمة المرور">
                                                                        <button
                                                                            onClick={() => setShowPasswordForm(true)}
                                                                            className="p-2 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                                                                        >
                                                                            <LockClosedIcon className="h-5 w-5" />
                                                                        </button>
                                                                    </Tooltip>
                                                                    {/* زر الإشعارات */}
                                                                    <Tooltip text={profile.notifications ? 'إيقاف الإشعارات' : 'تفعيل الإشعارات'}>
                                                                        <button
                                                                            onClick={toggleNotifications}
                                                                            className={`p-2 rounded-lg border ${
                                                                                profile.notifications
                                                                                    ? 'text-green-600 hover:text-green-700 dark:text-green-500 dark:hover:text-green-400 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                                                                                    : 'text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                                                                            }`}
                                                                        >
                                                                            <BellIcon className="h-5 w-5" />
                                                                        </button>
                                                                    </Tooltip>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* إرشادات للمستخدم */}
                                                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6">
                                                        <h4 className="text-base font-medium text-blue-900 dark:text-blue-100 mb-4 flex items-center">
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                                            </svg>
                                                            إرشادات مهمة
                                                        </h4>
                                                        <ul className="space-y-3 text-sm text-blue-900 dark:text-blue-100">
                                                            <li className="flex items-center">
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                                </svg>
                                                                لتغيير الصورة الشخصية، حرك المؤشر فوق الصورة واضغط على أيقونة القلم
                                                            </li>
                                                            <li className="flex items-center">
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                                </svg>
                                                                قم باختيار الصورة المناسبة التي تريدها من القائمة التي ستظهر امامك
                                                            </li>
                                                            <li className="flex items-center">
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                                </svg>
                                                                لتعديل معلوماتك الشخصية، اضغط على أيقونة القلم بجوار اسمك
                                                            </li>
                                                            <li className="flex items-center">
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                                </svg>
                                                                تأكد من تفعيل بريدك الإلكتروني للحصول على كافة مميزات المنصة
                                                            </li>
                                                            <li className="flex items-center">
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                                </svg>
                                                                يمكنك إضافة نبذة شخصية لتعريف الآخرين بنفسك
                                                            </li>
                                                            <li className="flex items-center">
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                                </svg>
                                                                رقم الجوال يجب أن يبدأ بـ 05 ويتكون من 10 أرقام
                                                            </li>
                                                        </ul>
                                                    </div>
                                                </div>
                                            )}
                                            {tab.id === 'courses' && (
                                                <div className="p-4 bg-white dark:bg-gray-800">
                                                    {loading ? <CoursesSkeleton /> : <MyCourses />}
                                                </div>
                                            )}
                                            {tab.id === 'wallet' && (
                                                <div className="p-4 bg-white dark:bg-gray-800">
                                                    {loading ? <PaymentsSkeleton /> : <Payments />}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </nav>
                    </div>

                    {/* محتوى التاب للنسخة المكتبية */}
                    {!isMobile && activeTab && (
                        <div className="p-6">
                            {activeTab === 'profile' && (
                                <div className="space-y-8">
                                    {/* الصورة الشخصية */}
                                    <div className="flex items-center space-x-4 space-x-reverse">
                                        <div className="relative group">
                                            <div className="w-24 h-24 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                                                {profile.avatar ? (
                                                    <img 
                                                        src={avatarOptions.find(a => a.id === profile.avatar)?.src || '/images/avatars/avatar1.png'} 
                                                        alt="الصورة الشخصية" 
                                                        className="w-full h-full object-cover"
                                                    />
                                                ) : (
                                                    <UserCircleIcon className="w-full h-full text-gray-400 dark:text-gray-500" />
                                                )}
                                            </div>
                                            <button
                                                onClick={handleAvatarClick}
                                                className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 text-white opacity-0 group-hover:opacity-100 transition-opacity rounded-full"
                                            >
                                                <PencilIcon className="h-6 w-6" />
                                            </button>
                                        </div>
                                        
                                        <div className="flex-1">
                                            {isEditing ? (
                                                <div className="space-y-4">
                                                    <div>
                                                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                            الاسم
                                                        </label>
                                                        <input
                                                            type="text"
                                                            id="name"
                                                            value={profile.name}
                                                            onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                                                            className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 focus:border-black dark:focus:border-white focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                            البريد الإلكتروني
                                                        </label>
                                                        <input
                                                            type="email"
                                                            id="email"
                                                            value={profile.email}
                                                            onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                                                            className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 focus:border-black dark:focus:border-white focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                            disabled
                                                        />
                                                    </div>
                                                    <div>
                                                        <label htmlFor="phone" className="block text-base font-medium text-gray-700 dark:text-gray-200 mb-2">
                                                            رقم الجوال
                                                        </label>
                                                        <input
                                                            type="tel"
                                                            id="phone"
                                                            value={profile.phone || ''}
                                                            onChange={handlePhoneChange}
                                                            placeholder="05XXXXXXXX"
                                                            maxLength={10}
                                                            className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 focus:border-black dark:focus:border-white focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                            dir="ltr"
                                                        />
                                                    </div>
                                                    <div className="flex items-center justify-between">
                                                        <div>
                                                            {isEditing ? (
                                                                <div className="flex gap-2">
                                                                    <Tooltip text="حفظ التغييرات">
                                                                        <button
                                                                            onClick={handleSaveClick}
                                                                            className="p-2 text-green-600 hover:text-green-700 dark:text-green-500 dark:hover:text-green-400 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800"
                                                                            disabled={loading}
                                                                        >
                                                                            <CheckCircleIcon className="h-5 w-5" />
                                                                        </button>
                                                                    </Tooltip>
                                                                    <Tooltip text="إلغاء التعديل">
                                                                        <button
                                                                            onClick={handleCancelEdit}
                                                                            className="p-2 text-red-600 hover:text-red-700 dark:text-red-500 dark:hover:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800"
                                                                        >
                                                                            <XIcon className="h-5 w-5" />
                                                                        </button>
                                                                    </Tooltip>
                                                                </div>
                                                            ) : (
                                                                <Tooltip text="تعديل البيانات">
                                                                    <button
                                                                        onClick={handleEditClick}
                                                                        className="p-2 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                                                                    >
                                                                        <PencilIcon className="h-5 w-5" />
                                                                    </button>
                                                                </Tooltip>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div>
                                                    <div className="flex items-center justify-between">
                                                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                                                            {profile.name}
                                                        </h3>
                                                        <Tooltip text="تعديل البيانات">
                                                            <button
                                                                onClick={handleEditClick}
                                                                className="p-2 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                                                            >
                                                                <PencilIcon className="h-5 w-5" />
                                                            </button>
                                                        </Tooltip>
                                                    </div>
                                                    <div className="mt-1 grid md:grid-cols-3 gap-6">
                                                        <div>
                                                            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                                البريد الإلكتروني
                                                            </div>
                                                            <div className="flex items-center">
                                                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                                                    {profile.email}
                                                                </p>
                                                                {profile.isEmailVerified ? (
                                                                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                                                                ) : (
                                                                    <div className="flex items-center mr-2">
                                                                        <Tooltip text="إعادة إرسال رابط التفعيل">
                                                                            <button
                                                                                onClick={handleResendVerification}
                                                                                className="mr-2 text-sm text-primary-600 hover:text-primary-500 dark:text-primary-400"
                                                                            >
                                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
                                                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                                                </svg>
                                                                            </button>
                                                                        </Tooltip>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                                رقم الجوال
                                                            </div>
                                                            <div className="text-sm text-gray-500 dark:text-gray-400">
                                                                <span style={{ direction: 'ltr', display: 'inline-block' }}>
                                                                    {profile.phone ? profile.phone.replace(/(\d{3})(\d{3})(\d{4})/, '$1 $2 $3') : 'لم يتم إضافة رقم الجوال'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                                تاريخ التسجيل
                                                            </div>
                                                            <div className="text-sm text-gray-500 dark:text-gray-400">
                                                                {new Date(profile.joinDate).toLocaleDateString('ar', { 
                                                                    year: 'numeric',
                                                                    month: '2-digit',
                                                                    day: '2-digit'
                                                                })}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* النبذة الشخصية */}
                                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
                                        <div className="flex items-center justify-between p-4">
                                            <h4 className="text-base font-medium text-gray-900 dark:text-white">النبذة الشخصية</h4>
                                            {!isEditingBio && (
                                                <Tooltip text="تعديل النبذة">
                                                    <button
                                                        onClick={() => setIsEditingBio(true)}
                                                        className="p-2 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                                                    >
                                                        <PencilIcon className="h-5 w-5" />
                                                    </button>
                                                </Tooltip>
                                            )}
                                            {isEditingBio && (
                                                <div className="flex gap-2">
                                                    <Tooltip text="حفظ النبذة">
                                                        <button
                                                            onClick={handleSaveBio}
                                                            className="p-2 text-green-600 hover:text-green-700 dark:text-green-500 dark:hover:text-green-400 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800"
                                                            disabled={loading}
                                                        >
                                                            <CheckCircleIcon className="h-5 w-5" />
                                                        </button>
                                                    </Tooltip>
                                                    <Tooltip text="إلغاء التعديل">
                                                        <button
                                                            onClick={handleCancelBio}
                                                            className="p-2 text-red-600 hover:text-red-700 dark:text-red-500 dark:hover:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800"
                                                        >
                                                            <XIcon className="h-5 w-5" />
                                                        </button>
                                                    </Tooltip>
                                                </div>
                                            )}
                                        </div>
                                        <div className="px-4 pb-4">
                                            {isEditingBio ? (
                                                <ReactQuill
                                                    theme="snow"
                                                    value={profile.bio}
                                                    onChange={(content) => setProfile({ ...profile, bio: content })}
                                                    placeholder="اكتب نبذة عن نفسك..."
                                                    className="[&_.ql-container]:mb-0 [&_.ql-container]:h-auto [&_.ql-editor]:h-auto [&_.ql-editor]:min-h-[150px] [&_.ql-editor]:max-h-none [&_.ql-editor]:text-right [&_.ql-editor]:direction-rtl [&_.ql-toolbar]:direction-rtl [&_.ql-toolbar]:bg-white [&_.ql-toolbar]:dark:bg-gray-700 [&_.ql-toolbar]:dark:text-white [&_.ql-toolbar_.ql-stroke]:dark:stroke-white [&_.ql-toolbar_.ql-fill]:dark:fill-white [&_.ql-toolbar_.ql-picker-label]:dark:text-white [&_.ql-toolbar_.ql-picker-item]:dark:text-white [&_.ql-toolbar_.ql-picker-options]:dark:bg-gray-700 [&_.ql-toolbar.ql-snow]:border-gray-300 [&_.ql-container.ql-snow]:border-gray-300 [&_.ql-toolbar.ql-snow]:dark:border-gray-600 [&_.ql-container.ql-snow]:dark:border-gray-600 [&_.ql-editor]:text-gray-900 [&_.ql-editor]:dark:text-white [&_.ql-editor]:text-sm"
                                                    modules={{
                                                        toolbar: [
                                                            ['bold', 'italic', 'underline'],
                                                            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                                                            ['clean']
                                                        ]
                                                    }}
                                                />
                                            ) : (
                                                <div className="prose dark:prose-invert max-w-none text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-700/50 p-3 rounded-lg">
                                                    {profile.bio ? (
                                                        <div className="text-gray-900 dark:text-white" dangerouslySetInnerHTML={{ __html: profile.bio }} />
                                                    ) : (
                                                        <p className="text-gray-900 dark:text-white">لم يتم إضافة نبذة بعد</p>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* كلمة المرور والإشعارات */}
                                    <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg">
                                        <div className="prose dark:prose-invert max-w-none text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700/50 p-3 rounded-lg">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center">
                                                    <LockClosedIcon className="h-6 w-6 text-gray-500 dark:text-gray-400 ml-2" />
                                                    <h2 className="text-lg font-medium text-gray-900 dark:text-white">كلمة المرور والإشعارات</h2>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {/* زر تغيير كلمة المرور */}
                                                    <Tooltip text="تغيير كلمة المرور">
                                                        <button
                                                            onClick={() => setShowPasswordForm(true)}
                                                            className="p-2 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                                                        >
                                                            <LockClosedIcon className="h-5 w-5" />
                                                        </button>
                                                    </Tooltip>
                                                    {/* زر الإشعارات */}
                                                    <Tooltip text={profile.notifications ? 'إيقاف الإشعارات' : 'تفعيل الإشعارات'}>
                                                        <button
                                                            onClick={toggleNotifications}
                                                            className={`p-2 rounded-lg border ${
                                                                profile.notifications
                                                                    ? 'text-green-600 hover:text-green-700 dark:text-green-500 dark:hover:text-green-400 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                                                                    : 'text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                                                            }`}
                                                        >
                                                            <BellIcon className="h-5 w-5" />
                                                        </button>
                                                    </Tooltip>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* إرشادات للمستخدم */}
                                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6">
                                        <h4 className="text-base font-medium text-blue-900 dark:text-blue-100 mb-4 flex items-center">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                            </svg>
                                            إرشادات مهمة
                                        </h4>
                                        <ul className="space-y-3 text-sm text-blue-900 dark:text-blue-100">
                                            <li className="flex items-center">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                </svg>
                                                لتغيير الصورة الشخصية، حرك المؤشر فوق الصورة واضغط على أيقونة القلم
                                            </li>
                                            <li className="flex items-center">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                </svg>
                                                قم باختيار الصورة المناسبة التي تريدها من القائمة التي ستظهر امامك
                                            </li>
                                            <li className="flex items-center">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                </svg>
                                                لتعديل معلوماتك الشخصية، اضغط على أيقونة القلم بجوار اسمك
                                            </li>
                                            <li className="flex items-center">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                </svg>
                                                تأكد من تفعيل بريدك الإلكتروني للحصول على كافة مميزات المنصة
                                            </li>
                                            <li className="flex items-center">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                </svg>
                                                يمكنك إضافة نبذة شخصية لتعريف الآخرين بنفسك
                                            </li>
                                            <li className="flex items-center">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                </svg>
                                                رقم الجوال يجب أن يبدأ بـ 05 ويتكون من 10 أرقام
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            )}
                            {activeTab === 'courses' && (
                                <div>
                                    {loading ? <CoursesSkeleton /> : <MyCourses />}
                                </div>
                            )}
                            {activeTab === 'wallet' && (
                                <div>
                                    {loading ? <PaymentsSkeleton /> : <Payments />}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            
            {/* مودال اختيار الصورة الرمزية */}
            <Modal
                isOpen={showAvatarModal}
                onClose={() => setShowAvatarModal(false)}
                title="اختيار صورة رمزية"
                size="md"
            >
                <div className="p-4">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        {avatarOptions.map((avatar) => {
                            const isSelected = selectedAvatar === avatar.id;
                            return (
                                <div 
                                    key={avatar.id} 
                                    className={`relative cursor-pointer rounded-full overflow-hidden ${isSelected ? 'ring-4 ring-red-500' : 'ring-2 ring-gray-300 dark:ring-gray-700'}`}
                                    onClick={() => handleAvatarSelect(avatar)}
                                    style={{ width: '80px', height: '80px', margin: '0 auto' }}
                                >
                                    <img 
                                        src={avatar.src} 
                                        alt={avatar.alt} 
                                        className="w-full h-full object-cover"
                                    />
                                    {isSelected && (
                                        <>
                                            <div className="absolute inset-0 border-4 border-red-500 rounded-full"></div>
                                            <div className="absolute bottom-0 right-0 bg-red-500 rounded-full p-1 shadow-md">
                                                <CheckCircleIcon className="h-4 w-4 text-white" />
                                            </div>
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    <div className="mt-6 flex justify-end space-x-3 space-x-reverse">
                        <button
                            onClick={() => setShowAvatarModal(false)}
                            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                            إلغاء
                        </button>
                        <button
                            onClick={handleSaveAvatar}
                            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none"
                        >
                            تأكيد
                        </button>
                    </div>
                </div>
            </Modal>
            
            <Modal
                isOpen={showPasswordForm}
                onClose={() => setShowPasswordForm(false)}
                title="تغيير كلمة المرور"
                size="md"
            >
                <form onSubmit={handlePasswordChange} className="mt-4 space-y-4">
                    <div>
                        <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                            كلمة المرور الحالية
                        </label>
                        <input
                            type="password"
                            id="currentPassword"
                            value={passwordData.currentPassword}
                            onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                            className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 focus:border-black dark:focus:border-white focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            required
                        />
                    </div>
                    <div>
                        <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                            كلمة المرور الجديدة
                        </label>
                        <input
                            type="password"
                            id="newPassword"
                            value={passwordData.newPassword}
                            onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                            className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 focus:border-black dark:focus:border-white focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            required
                        />
                    </div>
                    <div>
                        <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                            تأكيد كلمة المرور الجديدة
                        </label>
                        <input
                            type="password"
                            id="confirmPassword"
                            value={passwordData.confirmPassword}
                            onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                            className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 focus:border-black dark:focus:border-white focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            required
                        />
                    </div>
                </form>
                <div className="mt-6 sm:flex sm:flex-row-reverse gap-2">
                    <button
                        type="submit"
                        onClick={handlePasswordChange}
                        className="w-full inline-flex justify-center rounded-md bg-black dark:bg-white px-3 py-2 text-sm font-semibold text-white dark:text-black shadow-sm hover:bg-gray-800 dark:hover:bg-gray-200 sm:w-auto"
                    >
                        حفظ التغييرات
                    </button>
                    <button
                        type="button"
                        className="mt-3 w-full inline-flex justify-center rounded-md bg-white dark:bg-gray-800 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 sm:mt-0 sm:w-auto"
                        onClick={() => setShowPasswordForm(false)}
                    >
                        إلغاء
                    </button>
                </div>
            </Modal>
        </div>
    );
};
